import React, { useEffect, useRef, useState } from "react";
import { normalizeHex } from "../lib/harmonicPalette";
import ColorBlockPicker from "./ColorBlockPicker";

/**
 * Swatch + hex. The swatch opens a grid of color blocks (same on desktop
 * and mobile) instead of the native OS color wheel.
 */
const ColorField = ({
  color,
  onCommit,
  colorLabel,
  hexLabel,
  className = "color-input-group",
  onGroupClick,
  disabled = false,
  showColorInput = true,
}) => {
  const committed = normalizeHex(color) || "#000000";
  const [draft, setDraft] = useState(committed);
  const [open, setOpen] = useState(false);
  const hexFocusedRef = useRef(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!hexFocusedRef.current) {
      setDraft(committed);
    }
  }, [committed]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const commitIfHex = (value) => {
    const next = normalizeHex(value);
    if (!next || next === committed) return;
    onCommit(next);
  };

  const stop = (event) => event.stopPropagation();
  const selectGroup = (event) => {
    stop(event);
    if (onGroupClick) onGroupClick();
  };

  return (
    <div className={className} ref={rootRef} onClick={onGroupClick}>
      {showColorInput && (
        <div className="color-picker-wrap">
          <button
            type="button"
            className="color-picker"
            style={{ backgroundColor: committed }}
            data-color={committed}
            aria-label={colorLabel}
            aria-haspopup="listbox"
            aria-expanded={open}
            title={colorLabel}
            disabled={disabled}
            onClick={(event) => {
              selectGroup(event);
              setOpen((prev) => !prev);
            }}
            onMouseDown={stop}
          />
          {open && !disabled && (
            <ColorBlockPicker
              value={committed}
              extraColors={[committed]}
              onSelect={(hex) => {
                commitIfHex(hex);
                setOpen(false);
              }}
            />
          )}
        </div>
      )}
      <input
        type="text"
        className="color-text-input"
        value={draft}
        aria-label={hexLabel}
        spellCheck={false}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        disabled={disabled}
        maxLength={8}
        onChange={(event) => {
          const value = event.target.value;
          setDraft(value);
          commitIfHex(value);
        }}
        onFocus={(event) => {
          hexFocusedRef.current = true;
          event.target.select();
        }}
        onBlur={() => {
          hexFocusedRef.current = false;
          const next = normalizeHex(draft);
          if (next) {
            commitIfHex(draft);
            setDraft(next);
          } else {
            setDraft(committed);
          }
        }}
        onClick={selectGroup}
        onMouseDown={stop}
      />
    </div>
  );
};

export default ColorField;
